import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { groq } from 'next-sanity';
import LiveQuery from 'next-sanity/preview/live-query';
import React, { CSSProperties, ReactElement } from 'react';

import { LinkButtonRound } from '@components/Buttons/LinkButton';
import { ArticleLayout } from '@components/Layouts';
import { PortableTextBlockRenderer } from '@components/Typography/PortableText';
import { sanityFetch } from '@lib/sanity.fetch';

import type { PortableTextBlock } from '@portabletext/types';

export interface PageData {
  title: string;
  metaTitle: string;
  metaDescription: string;
  slug: string;
  content: PortableTextBlock | PortableTextBlock[];
  publishDate: string;
}

const query = groq`*[_type == "article" && slug.current match $slug && (!(_id in path("drafts.**")) || $isPreview)]
{
  title,
  metaTitle,
  metaDescription,
  content,
  "slug": slug.current,
  publishDate
}[0]`;

// eslint-disable-next-line no-restricted-syntax
export default function ArticlePage({
  draftMode,
  pageData,
}: {
  draftMode: boolean;
  pageData: PageData;
}) {
  const imageStyles: CSSProperties = {
    objectFit: 'contain',
    filter: 'grayscale(100%)',
  };

  return (
    <LiveQuery enabled={draftMode} query={query} initialData={pageData}>
      <Head>
        <title key="title">{pageData.metaTitle}</title>
        <meta name="title" content={pageData.metaTitle} key="metatitle" />
        <meta
          name="description"
          content={pageData.metaDescription}
          key="metadescription"
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}${pageData.slug}`}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: pageData.title,
              datePublished: pageData.publishDate,
              dateModified: pageData.publishDate,
              author: [
                {
                  '@type': 'Person',
                  name: 'Pip Monk',
                  url: `${process.env.NEXT_PUBLIC_SERVER_URL}/about`,
                },
              ],
            }),
          }}
        />

        <meta property="og:title" content={pageData.title} key="ogtitle" />
        <meta
          property="og:url"
          content={`${process.env.NEXT_PUBLIC_SERVER_URL}${pageData.slug}`}
          key="ogurl"
        />
        <meta
          property="og:image"
          content={`${process.env.NEXT_PUBLIC_SERVER_URL}/images/Footer logo-12.png`}
          key="ogimage"
        />
        <meta property="og:type" content="article" key="ogtype" />
        <meta
          property="og:description"
          content={pageData.metaDescription}
          key="ogdescription"
        />
      </Head>

      <>
        <h1>{pageData.title}</h1>
        <PortableTextBlockRenderer content={pageData.content} />
        <LinkButtonRound className='mx-auto my-5' href="/contact">contact now</LinkButtonRound>
      </>
    </LiveQuery>
  );
}

ArticlePage.getLayout = function getLayout(page: ReactElement) {
  return <ArticleLayout>{page}</ArticleLayout>;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = await sanityFetch<string[]>({
    draftMode: false,
    query: `//groq
    *[_type == "article" && !(_id in path("drafts.**"))]{"slug": slug.current}.slug
    `,
  });

  const paths = slugs.map((slug) => ({
    params: {
      article: slug.slice(slug.indexOf('/', 1) + 1),
    },
  }));

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({
  draftMode = false,
  params,
}) => {
  const pageData = await sanityFetch<PageData>({
    draftMode,
    query,
    params: { slug: params?.article?.toString(), isPreview: draftMode },
  });

  return {
    props: {
      pageData,
    },
  };
};
